/* eslint-disable no-unused-vars */
import { ErrorMessage, Field, Form, Formik } from "formik";
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import "../../assets/css/registerForm.css";
import SelectInput from "../../components/form/SelectInput.jsx";
import TextInput from "../../components/form/TextInput.jsx";
import DatePicker from "../../components/ui/DatePicker/DatePicker.jsx";
import { useFormProfileOptions } from "../../constants/form-profile.js";

import { signIn, refreshToken } from "../../features/auth/utils/auth.util.js";
import { useLineUser } from "../../features/line/hooks/useLineUser";
import { CreateCustomer } from "../../services/modules/verify.js";
import { useAccountRegisterStore } from "../../stores/useAccountRegisterStore";
import {
  getFormInitialValues,
  getFormSchema,
  mapPayloadToCreateCustomerForAunjaiAPI,
} from "../../utils/form.js";
import Header from "../Header/HeaderComponent.jsx";
import provinceJson from "../Json/allprovince.json";
import FormPdpa from "./FormPdpa.jsx";
import { useTranslation } from "react-i18next";

const DEFAULT_USER_LINE_ID = "";

const FormRegister = () => {
  const { MARITAL_STATUS_OPTIONS } = useFormProfileOptions();
  const [, setLoading] = useState(false);
  const { t } = useTranslation("register");
  const { lineProfile } = useLineUser();

  const userLineId = lineProfile?.userId;

  const navigate = useNavigate();
  const handleBack = () => {
    navigate(-1);
  };

  const phoneNumber = useAccountRegisterStore((state) => state.phoneNumber);

  const [isPdpaVisible, setIsPdpaVisible] = useState(false);

  const [formValues, setFormValues] = useState(null);

  const isForeigner = false;

  const formInitialValues = getFormInitialValues(false, {
    idCard: null,
    dob: null,
  });

  // Override phoneNumber initial value with the verified phone from store
  formInitialValues.phoneNumber = phoneNumber || "";

  const handleSubmitForm = (values) => {
    const province = provinceData.find((p) => p.id === parseInt(values.province));
    const district = province?.amphure?.find((d) => d.id === parseInt(values.district));
    const subDistrict = district?.tambon?.find((s) => s.id === parseInt(values.subDistrict));

    const formValuesWithNames = {
      ...values,
      provinceName: province?.name_th || "",
      districtName: district?.name_th || "",
      subDistrictName: subDistrict?.name_th || "",
    };

    setFormValues(formValuesWithNames);

    setIsPdpaVisible(true);
  };

  const provinceData = provinceJson;
  const [selectedProvince, setSelectedProvince] = useState(null);
  const [districtOptions, setDistrictOptions] = useState([]);
  const [subDistrictOptions, setSubDistrictOptions] = useState([]);

  const validationSchema = getFormSchema(false);

  const handleProvinceChange = (event, setFieldValue) => {
    const selectedProvinceId = event.target.value;
    setSelectedProvince(selectedProvinceId);

    const province = provinceData.find(
      (p) => p.id === parseInt(selectedProvinceId)
    );
    if (province) {
      setFieldValue("province", selectedProvinceId);
      setDistrictOptions(province.amphure || []);
      setSubDistrictOptions([]);
      setFieldValue("district", "");
      setFieldValue("subDistrict", "");
    } else {
      setDistrictOptions([]);
      setSubDistrictOptions([]);
      setFieldValue("district", "");
      setFieldValue("subDistrict", "");
    }
  };

  const handleDistrictChange = (event, setFieldValue) => {
    const selectedDistrictId = event.target.value;
    const selectedDistrict = selectedProvince
      ? provinceData
          .find((p) => p.id === parseInt(selectedProvince))
          ?.amphure.find((d) => d.id === parseInt(selectedDistrictId))
      : null;

    if (selectedDistrict) {
      setSubDistrictOptions(selectedDistrict.tambon || []);
      setFieldValue("district", selectedDistrictId);
      setFieldValue("subDistrict", "");
    }
  };

  const handleSubDistrictChange = (event, setFieldValue) => {
    const selectedSubDistrictId = event.target.value;
    const selectedSubDistrict = subDistrictOptions.find(
      (subdistrict) => subdistrict.id === parseInt(selectedSubDistrictId)
    );

    if (selectedSubDistrict) {
      setFieldValue("subDistrict", selectedSubDistrictId);
      setFieldValue("postalCode", selectedSubDistrict.zip_code);
    }
  };

  const handlePdpaConsent = async (e) => {
    e.preventDefault();

    if (!formValues) return;

    const mappedData = mapPayloadToCreateCustomerForAunjaiAPI({
      formValues,
      isForeigner: 0,
      targetLineUUID: userLineId || DEFAULT_USER_LINE_ID,
    });

    setLoading(true);

    // Generate JWT token before creating customer (required for auth)
    await refreshToken({
      idCard: formValues.idCard,
      idPassport: formValues.idPassport,
      isForeigner: 0,
    });

    const response = await CreateCustomer({ body: mappedData });

    setLoading(false);

    const wasCustomerCreationSuccessful = response.resultCode === "20000";

    if (wasCustomerCreationSuccessful) {
      await signIn({
        idCard: formValues.idCard,
        idPassport: formValues.idPassport,
        dateOfBirth: formValues.dob,
        isForeigner: 0,
      });

      setIsPdpaVisible(false);

      navigate("/register/success");
    } else {
      setIsPdpaVisible(false);

      alert(t("alert2"));
    }
  };
  const handlePdpaDecline = () => {
    setIsPdpaVisible(false); // Close PDPA modal without submitting
  };
  const lang = localStorage.getItem("i18nextLng")?.startsWith("en")
    ? "en"
    : "th";

  return (
    <div id="PageFormRegister">
      <div className="wrapContent">
        <div id="pFormRegister">
          <div className="bContent">
            {!isPdpaVisible && (
              <>
                <Header title={t("register")} showIcon onBack />
                <Formik
                  initialValues={formInitialValues}
                  validationSchema={validationSchema}
                  onSubmit={(values) => {
                    handleSubmitForm(values);
                  }}
                >
                  {({ handleSubmit, setFieldValue, values, errors }) => {
                    return (
                      <>
                        <Form className="bForm" onSubmit={handleSubmit}>
                          <h2 className="text-lg font-bold">{t("detail")}</h2>
                          <SelectInput
                            label={t("start")}
                            name="title"
                            options={[
                              { value: "นาย", label: t("mr") },
                              { value: "นาง", label: t("mrs") },
                              { value: "นางสาว", label: t("miss") },
                            ]}
                            placeholder={t("selectStart")}
                            errorMessage={errors.title}
                            required
                          />
                          {/* ชื่อ (ภาษาไทย) */}
                          <TextInput
                            label={t("nameTh")}
                            name="firstNameTh"
                            placeholder={t("name")}
                            errorMessage={errors.firstNameTh}
                            required
                          />
                          {/* ชื่อกลาง(ไทย) — hidden, default '-' */}
                          <TextInput
                            label={t("surTh")}
                            name="lastNameTh"
                            placeholder={t("sur")}
                            errorMessage={errors.lastNameTh}
                            required
                          />
                          <TextInput
                            label={t("nameEn")}
                            name="firstNameEn"
                            placeholder={t("name")}
                            errorMessage={errors.firstNameEn}
                          />
                          {/* ชื่อกลาง(อังกฤษ) — hidden, default '-' */}
                          <TextInput
                            label={t("surEn")}
                            name="lastNameEn"
                            placeholder={t("sur")}
                            errorMessage={errors.lastNameEn}
                          />
                          {/* วัน/เดือน/ปีเกิด — hidden, default '2000-01-01' */}
                          {/* หมายเลขบัตรประชาชน — hidden, default '0000000000000' */}
                          {/* เบอร์โทรศัพท์ (read-only, verified from register page) */}
                          <TextInput
                            label={t("tel")}
                            name="phoneNumber"
                            placeholder={t("telNum")}
                            errorMessage={errors.phoneNumber}
                            disabled
                            required
                          />
                          {/* อีเมล์ */}
                          <TextInput
                            label={t("email")}
                            name="email"
                            placeholder={t("email")}
                            errorMessage={errors.email}
                            required
                          />
                          {/* สถานะ — hidden, default 'โสด' */}

                          {/* ที่อยู่ปัจจุบัน */}
                          <TextInput
                            label={t("placeNow")}
                            name="address"
                            placeholder={t("placeInput")}
                            errorMessage={errors.address}
                            required
                          />
                          <div className="bInput">
                            <label className="tText">{t("province")} <span style={{ color: "red" }}>*</span></label>
                            <Field
                              as="select"
                              name="province"
                              onChange={(e) => handleProvinceChange(e, setFieldValue)}
                            >
                              <option value="">{t("selectProvince")}</option>
                              {provinceData.map((province) => (
                                <option key={province.id} value={province.id}>
                                  {province.name_th}
                                </option>
                              ))}
                            </Field>
                            <ErrorMessage name="province" component="div" className="error" />
                          </div>
                          <div className="bInput">
                            <label className="tText">{t("city")} <span style={{ color: "red" }}>*</span></label>
                            <Field
                              as="select"
                              name="district"
                              onChange={(e) => handleDistrictChange(e, setFieldValue)}
                            >
                              <option value="">{t("selectCity")}</option>
                              {districtOptions.map((district) => (
                                <option key={district.id} value={district.id}>
                                  {district.name_th}
                                </option>
                              ))}
                            </Field>
                            <ErrorMessage name="district" component="div" className="error" />
                          </div>
                          <div className="bInput">
                            <label className="tText">{t("subUrban")} <span style={{ color: "red" }}>*</span></label>
                            <Field
                              as="select"
                              name="subDistrict"
                              onChange={(e) => handleSubDistrictChange(e, setFieldValue)}
                            >
                              <option value="">{t("selectSubUrban")}</option>
                              {subDistrictOptions.map((sub) => (
                                <option key={sub.id} value={sub.id}>
                                  {sub.name_th}
                                </option>
                              ))}
                            </Field>
                            <ErrorMessage name="subDistrict" component="div" className="error" />
                          </div>
                          <TextInput
                            label={t("post")}
                            name="postalCode"
                            placeholder={t("post")}
                            disabled
                          />

                          {!isPdpaVisible && (
                            <footer
                              style={{
                                height: 80,
                                margin: 0,
                                padding: "0 24px",
                              }}
                              className="footer"
                            >
                              <div className="bBtn bActionButton">
                                <button
                                  type="button"
                                  className="outline"
                                  onClick={handleBack}
                                >
                                  {t("back")}
                                </button>
                                <button type="submit">{t("next")}</button>
                              </div>
                            </footer>
                          )}
                        </Form>
                      </>
                    );
                  }}
                </Formik>
              </>
            )}
            {isPdpaVisible && (
              <FormPdpa
                onAgree={handlePdpaConsent}
                onDecline={handlePdpaDecline}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default FormRegister;
